import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Alert, Button } from 'reactstrap';
import Breadcrumbs from '../../shims/commonUiBreadcrumbs';
import TicketHeader from '../../components/tickets/TicketHeader';
import TicketDescription from '../../components/tickets/TicketDescription';
import CommentList from '../../components/tickets/CommentList';
import AttachmentList from '../../components/tickets/AttachmentList';
import AttachFilesForm from '../../components/tickets/AttachFilesForm';
import TicketDetailSkeleton from '../../components/tickets/TicketDetailSkeleton';
import AddCommentForm from '../../components/tickets/AddCommentForm';
import WatcherSection from '../../components/tickets/WatcherSection';
import { getTicket } from '../../api/ticketApi';

const POLL_MS = 30000;

export default function TicketDetailPage() {
  const navigate = useNavigate();
  const { ticketId } = useParams();
  const [ticket, setTicket] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);
  const addCommentFormRef = useRef(null);

  const fetchTicket = useCallback(async (silent = false) => {
    if (!ticketId) return;
    if (!silent) setIsLoading(true);
    setError(null);
    setErrorStatus(null);
    try {
      const data = await getTicket(ticketId);
      setTicket(data);
    } catch (err) {
      setError(err?.json?.message || err?.json?.error || 'Failed to load ticket.');
      setErrorStatus(err?.status);
    } finally {
      setIsLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    fetchTicket();
  }, [fetchTicket]);

  useEffect(() => {
    if (!ticketId || document.visibilityState === 'hidden') return;
    const id = setInterval(() => fetchTicket(true), POLL_MS);
    return () => clearInterval(id);
  }, [ticketId, fetchTicket]);

  const handleCommentAdded = (comment) => {
    setTicket((prev) => ({
      ...prev,
      comments: [...(prev?.comments || []), comment],
    }));
  };

  const handleReplyFocus = () => {
    addCommentFormRef.current?.scrollIntoView?.({ behavior: 'smooth' });
    setTimeout(() => {
      const input = document.querySelector('[aria-label="Add a comment"]');
      input?.focus?.();
    }, 300);
  };

  if (isLoading && !ticket) {
    return <TicketDetailSkeleton />;
  }

  if (error && (errorStatus === 404 || errorStatus === 403)) {
    return (
      <>
        <div className="d-flex mtop-1">
          <Breadcrumbs mainTitle="Ticket" />
        </div>
        <Alert color="danger" className="mb-2">
          {errorStatus === 404 ? 'Ticket not found. It may have been deleted.' : "You don't have access to this ticket."}
        </Alert>
        <Button tag={Link} to="/ticketing/tickets" color="primary">
          Back to ticket list
        </Button>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div className="d-flex mtop-1">
          <Breadcrumbs mainTitle="Ticket" />
        </div>
        <Alert color="danger" toggle={() => setError(null)} className="mb-2">
          {error}
        </Alert>
        <Button tag={Link} to="/ticketing/tickets" color="primary">
          Back to ticket list
        </Button>
      </>
    );
  }

  if (!ticket) return null;

  return (
    <>
      <div className="d-flex mtop-1 flex-wrap align-items-center justify-content-between gap-2">
        <div className="d-flex flex-wrap align-items-center gap-2">
          <Button color="link" className="p-0 text-decoration-none" onClick={() => navigate('/ticketing/tickets')}>
            ← Back to tickets
          </Button>
          <Breadcrumbs mainTitle={`Ticket ${ticket.ticketId}`} />
        </div>
      </div>
      <TicketHeader
        ticketId={ticket.ticketId}
        subject={ticket.subject}
        status={ticket.status}
        createdAt={ticket.createdAt}
        updatedAt={ticket.updatedAt}
      />
      <TicketDescription description={ticket.description} />
      <AttachmentList ticketId={ticket.ticketId} attachments={ticket.attachments} />
      <AttachFilesForm
        ticketId={ticket.ticketId}
        existingCount={ticket.attachments?.length ?? 0}
        onUploaded={() => fetchTicket(true)}
      />
      <div ref={addCommentFormRef} className="mt-3">
        <CommentList comments={ticket.comments} onReply={handleReplyFocus} />
        <AddCommentForm ticketId={ticket.ticketId} onCommentAdded={handleCommentAdded} />
      </div>
      <WatcherSection ticketId={ticket.ticketId} />
    </>
  );
}
